import { type ReactNode, useEffect, useRef, useState } from 'react';
import { cn } from '@/utils/cn';

// Animates content height when children swap (skeleton → data, empty
// → data, etc.). The first measurement skips the transition because
// CSS can't interpolate from `auto` to a number; subsequent height
// changes interpolate over 250ms to match the Fade crossfade.

type Props = {
  children: ReactNode;
  className?: string;
  durationMs?: number;
};

function useMeasuredHeight(
  ref: React.RefObject<HTMLDivElement | null>
): number | undefined {
  const [height, setHeight] = useState<number | undefined>(undefined);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new globalThis.ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setHeight(entry.contentRect.height);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return height;
}

export function AnimatedHeight({
  children,
  className,
  durationMs = 250,
}: Props): ReactNode {
  const innerRef = useRef<HTMLDivElement>(null);
  const height = useMeasuredHeight(innerRef);
  return (
    <div
      className={cn('overflow-hidden transition-[height] ease-out', className)}
      style={{ height, transitionDuration: `${durationMs}ms` }}
    >
      <div ref={innerRef}>{children}</div>
    </div>
  );
}
